import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Submissions.css';
import '../components/AuthWarningModal.css';
import { CopyToast, useCopyToast } from '../components/CopyToast';
import { 
  IconEdit, 
  IconSearch, 
  IconChevronLeft, 
  IconChevronRight, 
  IconChevronsLeft, 
  IconChevronsRight,
  IconChevronDown,
  IconX,
  IconZoomIn,
  IconMinus,
  IconPlus,
  IconCopy
} from '@tabler/icons-react';

function formatUserIdPreview(userId) {
  if (userId == null || userId === '') return '—';
  const s = String(userId);
  if (s.length <= 5) return s;
  return `${s.slice(0, 5)}…`;
}

/** Clipboard API needs a secure context; fallback works on many HTTP / older browsers. */
function copyTextToClipboard(text) {
  const tryExecCommand = () => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;left:0;top:0;width:2px;height:2px;padding:0;border:none;outline:none;box-shadow:none;background:transparent;opacity:0;';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } finally {
      document.body.removeChild(ta);
    }
    return ok;
  };

  if (navigator.clipboard?.writeText && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => tryExecCommand());
  }
  return Promise.resolve(tryExecCommand());
}

const MIN_VIEWER_ZOOM = 0.5;
const MAX_VIEWER_ZOOM = 4;

const clampViewerZoom = (zoom) => Math.min(MAX_VIEWER_ZOOM, Math.max(MIN_VIEWER_ZOOM, zoom));

const getDistance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const getMidpoint = (a, b) => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

function Submissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [resending, setResending] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [viewingImage, setViewingImage] = useState(null);
  const [viewerZoom, setViewerZoom] = useState(1);
  const [viewerPan, setViewerPan] = useState({ x: 0, y: 0 });
  const viewerZoomRef = useRef(1);
  const viewerGestureRef = useRef({
    pointers: new Map(),
    lastDragPoint: null,
    lastPinchDistance: null,
    lastPinchMidpoint: null,
  });
  
  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '' });
  const [pendingAction, setPendingAction] = useState(null); // Store pending submission ID and action type
  const [copiedUserIdKey, setCopiedUserIdKey] = useState(null);
  const copyUserIdToast = useCopyToast();
  const [editingReceiptAmounts, setEditingReceiptAmounts] = useState([]);
  const [savingAmounts, setSavingAmounts] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('submissions');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  // Filter & Search state
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchUserId, setSearchUserId] = useState('');
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    viewerZoomRef.current = viewerZoom;
  }, [viewerZoom]);

  useEffect(() => {
    if (!viewingImage) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageViewer();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingImage]);

  useEffect(() => {
    if (!selectedSubmission) {
      setEditingReceiptAmounts([]);
      return;
    }

    setEditingReceiptAmounts(
      (selectedSubmission.receipts || []).map((receipt) =>
        receipt.amount === undefined || receipt.amount === null ? '' : String(receipt.amount)
      )
    );
  }, [selectedSubmission]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/receipts/history');
      if (response.data.success) {
        setSubmissions(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch submissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const upsertSubmissionInState = (updatedSubmission) => {
    if (!updatedSubmission) return;

    setSubmissions(prev => prev.map(s => (
      s.id === updatedSubmission.id
        ? {
            ...s,
            ...updatedSubmission,
            username: updatedSubmission.username ?? s.username ?? null,
          }
        : s
    )));

    setSelectedSubmission(prev => (
      prev?.id === updatedSubmission.id
        ? {
            ...prev,
            ...updatedSubmission,
            username: updatedSubmission.username ?? prev.username ?? null,
          }
        : prev
    ));
  };

  const refreshSubmissionsSilently = async (submissionId = null) => {
    try {
      const response = await api.get('/receipts/history');
      if (response.data.success) {
        const nextSubmissions = response.data.data || [];
        setSubmissions(nextSubmissions);

        const targetId = submissionId || selectedSubmission?.id;
        if (targetId) {
          const refreshedSubmission = nextSubmissions.find(s => s.id === targetId) || null;
          setSelectedSubmission(prev => (prev?.id === targetId ? refreshedSubmission : prev));
          return refreshedSubmission;
        }
      }
    } catch (err) {
      console.error('Failed to refresh submissions after update:', err);
    }

    return null;
  };

  const buildResendFeedback = (submission) => {
    const rewardStatus = submission?.rewards?.status;
    const submissionStatusLabel = getStatusLabel(submission?.status || 'pending');
    const rewardErrors = [
      submission?.rewards?.voucherError,
      submission?.rewards?.tokenError,
    ].filter(Boolean);

    if (rewardStatus === 'success') {
      return {
        title: 'Success',
        message: `Failed rewards were resent successfully. Submission status is now ${submissionStatusLabel}.`,
      };
    }

    if (rewardStatus === 'partial_failed') {
      return {
        title: 'Partial Success',
        message: `Resend completed, but some rewards still failed. Submission status is now ${submissionStatusLabel}.${rewardErrors.length ? ` Latest error: ${rewardErrors.join(' | ')}` : ''}`,
      };
    }

    return {
      title: 'Resend Completed',
      message: `The resend request finished, but the reward is still failing. Submission status remains ${submissionStatusLabel}.${rewardErrors.length ? ` Latest error: ${rewardErrors.join(' | ')}` : ''}`,
    };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
  };

  /** Ringgit display — matches PWA (RM 0.00, same as Malaysian Ringgit symbol) */
  const formatAmount = (amount) => {
    const n = parseFloat(amount);
    if (Number.isNaN(n)) return 'RM 0.00';
    return `RM ${n.toFixed(2)}`;
  };

  const copyUserId = async (userId, key) => {
    if (userId == null || userId === '') return;
    const text = String(userId);
    try {
      const ok = await copyTextToClipboard(text);
      if (ok) {
        setCopiedUserIdKey(key);
        window.setTimeout(() => setCopiedUserIdKey((k) => (k === key ? null : k)), 2000);
        copyUserIdToast.show();
      } else {
        setMessageModalContent({
          title: 'Copy failed',
          message: 'Could not copy to the clipboard. Your browser may block copying on this URL; try HTTPS or copy the ID manually from the network tab or logs.',
        });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Copy failed:', err);
      setMessageModalContent({
        title: 'Copy failed',
        message: err?.message || 'Could not copy to the clipboard.',
      });
      setShowMessageModal(true);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      processing: 'status-processing',
      processed: 'status-processed',
      approved: 'status-approved',
      rejected: 'status-rejected',
      failed: 'status-failed',
      partial_failed: 'status-partial-failed',
      reward_failed: 'status-reward-failed'
    };
    return statusClasses[status] || 'status-pending';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      processing: 'Processing',
      processed: 'Processed',
      approved: 'Approved',
      rejected: 'Rejected',
      failed: 'Failed',
      partial_failed: 'Partial Failed',
      reward_failed: 'Reward Failed'
    };
    return labels[status] || status;
  };

  const hasRewardErrors = (submission) => {
    return submission.rewards?.voucherError || submission.rewards?.tokenError;
  };

  const canResend = (submission) => {
    return ['partial_failed', 'reward_failed', 'failed'].includes(submission.status) && 
           hasRewardErrors(submission);
  };

  // Check if submission can be approved/rejected (pending manual review)
  const canReview = (submission) => {
    return submission.status === 'pending' && submission.awaitingReview === true;
  };

  const rewardsIntroLeadText = (s) => {
    if (canReview(s)) {
      return 'Pending approval — no rewards have been sent yet.';
    }
    const st = s.rewards?.status;
    if (st === 'success') {
      return 'All configured rewards for this claim were delivered successfully.';
    }
    if (st === 'partial_failed') {
      return canResend(s)
        ? 'One or more rewards did not send. See each block below.'
        : 'One or more rewards did not send. See each block below; resend may not be available for this claim.';
    }
    if (st === 'failed') {
      return 'Reward delivery failed. See each block below for errors.';
    }
    if (st === 'pending') {
      return 'Rewards are still being processed for this claim.';
    }
    return 'Voucher and token delivery status for this claim is shown in the table below.';
  };

  const rewardCell = (v) => {
    if (v == null) return '—';
    const s = String(v).trim();
    return s === '' ? '—' : s;
  };

  const getRewardsDetailRows = (s) => {
    const r = s.rewards || {};
    const rows = [];

    const includeVoucher = r.expectedTypes?.includes('VOUCHER') || s.voucherSent;
    if (includeVoucher) {
      const sent = !!(s.voucherSent || r.voucher);
      const err = r.voucherError;
      const nameBase = sent
        ? rewardCell(r.voucher?.voucherName || s.voucherSent?.ruleName)
        : rewardCell(r.attemptedVoucherName);
      const nft = sent ? rewardCell(s.voucherSent?.voucherNftId || r.voucher?.nftId) : '—';
      const sentAt = sent && r.voucherSentAt ? formatDate(r.voucherSentAt) : '—';
      let statusText = 'Not sent yet';
      let statusVariant = 'pending';
      if (sent) {
        statusText = 'Sent';
        statusVariant = 'success';
      } else if (err) {
        statusText = 'Failed';
        statusVariant = 'failed';
      } else if (canReview(s)) {
        statusText = 'Pending approval';
        statusVariant = 'pending';
      }
      const name = nameBase;
      const errorReason = err && !sent ? String(err).trim() : '';
      const title =
        err && !sent
          ? nameBase !== '—'
            ? `${nameBase}\n\n${err}`
            : err
          : sent && nameBase !== '—'
            ? nameBase
            : undefined;
      rows.push({
        key: 'voucher',
        typeChip: 'Voucher',
        name,
        nftId: nft,
        statusText,
        statusVariant,
        sentAt,
        errorReason,
        title,
      });
    }

    const includeToken = r.expectedTypes?.includes('TOKEN');
    if (includeToken) {
      const t = r.tokens;
      const sent = !!(t && !t.pending);
      const err = r.tokenError;
      let nameBase = '—';
      if (sent) {
        const label = t.tokenName || t.tokenType || 'Token';
        const amt = t.amount != null ? `${t.amount}` : '';
        nameBase = amt ? `${label} · ${amt}` : label;
      } else if (r.attemptedTokenInfo) {
        const a = r.attemptedTokenInfo.amount;
        const ty = r.attemptedTokenInfo.tokenType || r.attemptedTokenInfo.tokenName;
        nameBase = ty ? `${ty} · ${a ?? ''}` : rewardCell(a);
      }
      const nft = sent ? rewardCell(t.tokenId || t.vioTokenId || t.id) : '—';
      const sentAt = sent && r.tokensSentAt ? formatDate(r.tokensSentAt) : '—';
      let statusText = 'Not sent yet';
      let statusVariant = 'pending';
      if (sent) {
        statusText = 'Sent';
        statusVariant = 'success';
      } else if (err) {
        statusText = 'Failed';
        statusVariant = 'failed';
      } else if (canReview(s)) {
        statusText = 'Pending approval';
        statusVariant = 'pending';
      }
      const name = nameBase;
      const errorReason = err && !sent ? String(err).trim() : '';
      const title =
        err && !sent ? (nameBase !== '—' ? `${nameBase}\n\n${err}` : err) : undefined;
      rows.push({
        key: 'token',
        typeChip: 'Token',
        name,
        nftId: nft,
        statusText,
        statusVariant,
        sentAt,
        errorReason,
        title,
      });
    }

    return rows;
  };

  // Handle approve submission
  const handleApprove = (submissionId) => {
    setPendingAction({ type: 'approve', id: submissionId });
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    if (!pendingAction || pendingAction.type !== 'approve') return;
    const submissionId = pendingAction.id;
    
    setShowApproveModal(false);
    setApproving(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/approve`, {
        reviewedBy: 'admin'
      });
      
      if (response.data.success) {
        // Update the submission in the list
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? response.data.data : s
        ));
        // Update selected submission if it's open
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
        setMessageModalContent({ title: 'Success', message: 'Submission approved successfully!' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to approve submission:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to approve submission: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setApproving(false);
      setPendingAction(null);
    }
  };

  // Handle reject submission
  const handleReject = (submissionId) => {
    setPendingAction({ type: 'reject', id: submissionId });
    setRejectReason('');
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    if (!pendingAction || pendingAction.type !== 'reject') return;
    if (!rejectReason.trim()) {
      setMessageModalContent({ title: 'Error', message: 'Please provide a reason for rejection.' });
      setShowMessageModal(true);
      return;
    }
    
    const submissionId = pendingAction.id;
    setShowRejectModal(false);
    setRejecting(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/reject`, {
        reviewedBy: 'admin',
        rejectReason: rejectReason.trim()
      });
      
      if (response.data.success) {
        // Update the submission in the list
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? response.data.data : s
        ));
        // Update selected submission if it's open
        if (selectedSubmission?.id === submissionId) {
          setSelectedSubmission(response.data.data);
        }
        setMessageModalContent({ title: 'Success', message: 'Submission rejected.' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to reject submission:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to reject submission: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setRejecting(false);
      setPendingAction(null);
      setRejectReason('');
    }
  };

  const handleResend = (submissionId) => {
    setPendingAction({ type: 'resend', id: submissionId });
    setShowResendModal(true);
  };

  const confirmResend = async () => {
    if (!pendingAction || pendingAction.type !== 'resend') return;
    const submissionId = pendingAction.id;
    
    setShowResendModal(false);
    setResending(true);
    try {
      const response = await api.post(`/receipts/${submissionId}/resend-rewards`, {
        resendBy: 'admin'
      });
      
      if (response.data.success) {
        upsertSubmissionInState(response.data.data);
        const refreshedSubmission = await refreshSubmissionsSilently(submissionId);
        const latestSubmission = refreshedSubmission || response.data.data;
        setMessageModalContent(buildResendFeedback(latestSubmission));
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to resend rewards:', err);
      setMessageModalContent({ title: 'Error', message: 'Failed to resend rewards: ' + (err.response?.data?.error || err.message) });
      setShowMessageModal(true);
    } finally {
      setResending(false);
      setPendingAction(null);
    }
  };

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
  };

  const closeDetailModal = () => {
    setSelectedSubmission(null);
    setEditingReceiptAmounts([]);
  };

  const openImageViewer = (imageUrl) => {
    setViewerZoom(1);
    viewerZoomRef.current = 1;
    setViewerPan({ x: 0, y: 0 });
    viewerGestureRef.current.pointers.clear();
    setViewingImage(imageUrl);
  };

  const closeImageViewer = () => {
    setViewingImage(null);
    setViewerZoom(1);
    viewerZoomRef.current = 1;
    setViewerPan({ x: 0, y: 0 });
    viewerGestureRef.current.pointers.clear();
  };

  const setViewerZoomValue = (nextZoom) => {
    const clampedZoom = clampViewerZoom(nextZoom);
    viewerZoomRef.current = clampedZoom;
    setViewerZoom(clampedZoom);
    if (clampedZoom <= 1) {
      setViewerPan({ x: 0, y: 0 });
    }
  };

  const resetImageViewerTransform = () => {
    setViewerZoomValue(1);
    setViewerPan({ x: 0, y: 0 });
  };

  const handleViewerWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const direction = event.deltaY < 0 ? 1 : -1;
    setViewerZoomValue(viewerZoomRef.current + direction * 0.15);
  };

  const handleViewerPointerDown = (event) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    const gesture = viewerGestureRef.current;
    gesture.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    const points = Array.from(gesture.pointers.values());
    if (points.length === 1) {
      gesture.lastDragPoint = points[0];
      gesture.lastPinchDistance = null;
      gesture.lastPinchMidpoint = null;
    } else if (points.length === 2) {
      gesture.lastPinchDistance = getDistance(points[0], points[1]);
      gesture.lastPinchMidpoint = getMidpoint(points[0], points[1]);
      gesture.lastDragPoint = null;
    }
  };

  const handleViewerPointerMove = (event) => {
    const gesture = viewerGestureRef.current;
    if (!gesture.pointers.has(event.pointerId)) return;

    event.preventDefault();
    event.stopPropagation();
    gesture.pointers.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    const points = Array.from(gesture.pointers.values());
    if (points.length === 1) {
      const point = points[0];
      if (gesture.lastDragPoint && viewerZoomRef.current > 1) {
        const dx = point.clientX - gesture.lastDragPoint.clientX;
        const dy = point.clientY - gesture.lastDragPoint.clientY;
        setViewerPan((pan) => ({ x: pan.x + dx, y: pan.y + dy }));
      }
      gesture.lastDragPoint = point;
      return;
    }

    if (points.length === 2) {
      const distance = getDistance(points[0], points[1]);
      const midpoint = getMidpoint(points[0], points[1]);

      if (gesture.lastPinchDistance) {
        const nextZoom = viewerZoomRef.current * (distance / gesture.lastPinchDistance);
        setViewerZoomValue(nextZoom);
      }

      if (gesture.lastPinchMidpoint && viewerZoomRef.current > 1) {
        const dx = midpoint.x - gesture.lastPinchMidpoint.x;
        const dy = midpoint.y - gesture.lastPinchMidpoint.y;
        setViewerPan((pan) => ({ x: pan.x + dx, y: pan.y + dy }));
      }

      gesture.lastPinchDistance = distance;
      gesture.lastPinchMidpoint = midpoint;
    }
  };

  const handleViewerPointerEnd = (event) => {
    event.stopPropagation();
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch (err) {
      // The browser may already release capture when a touch gesture is cancelled.
    }

    const gesture = viewerGestureRef.current;
    gesture.pointers.delete(event.pointerId);
    const points = Array.from(gesture.pointers.values());

    if (points.length === 1) {
      gesture.lastDragPoint = points[0];
      gesture.lastPinchDistance = null;
      gesture.lastPinchMidpoint = null;
    } else {
      gesture.lastDragPoint = null;
      gesture.lastPinchDistance = null;
      gesture.lastPinchMidpoint = null;
    }
  };

  const handleViewerDoubleClick = (event) => {
    event.stopPropagation();
    if (viewerZoomRef.current > 1) {
      resetImageViewerTransform();
    } else {
      setViewerZoomValue(2);
    }
  };

  const handleReceiptAmountChange = (index, value) => {
    setEditingReceiptAmounts((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const getCurrentReceiptAmounts = () => {
    if (!selectedSubmission) return [];

    if (editingReceiptAmounts.length === (selectedSubmission.receipts || []).length) {
      return editingReceiptAmounts;
    }

    return (selectedSubmission.receipts || []).map((receipt) =>
      receipt.amount === undefined || receipt.amount === null ? '' : String(receipt.amount)
    );
  };

  const getEditedTotalAmount = () => {
    return getCurrentReceiptAmounts().reduce((sum, amount) => {
      const parsed = parseFloat(amount);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
  };

  const hasReceiptAmountChanges = () => {
    const currentAmounts = getCurrentReceiptAmounts();

    if (!selectedSubmission || currentAmounts.length !== (selectedSubmission.receipts || []).length) {
      return false;
    }

    return currentAmounts.some((amount, index) => {
      const parsed = parseFloat(amount);
      const original = parseFloat(selectedSubmission.receipts[index]?.amount) || 0;

      if (!Number.isFinite(parsed)) {
        return true;
      }

      return Math.abs(parsed - original) > 0.0001;
    });
  };

  const saveReceiptAmounts = async () => {
    if (!selectedSubmission || !canReview(selectedSubmission)) return;

    const currentAmounts = getCurrentReceiptAmounts();
    const invalidIndex = currentAmounts.findIndex((amount) => {
      const parsed = parseFloat(amount);
      return !Number.isFinite(parsed) || parsed < 0;
    });

    if (invalidIndex !== -1) {
      setMessageModalContent({
        title: 'Error',
        message: `Receipt ${invalidIndex + 1} amount must be a valid number greater than or equal to 0.`,
      });
      setShowMessageModal(true);
      return;
    }

    setSavingAmounts(true);
    try {
      const response = await api.post(`/receipts/${selectedSubmission.id}/update-amounts`, {
        updatedBy: 'admin',
        receipts: currentAmounts.map((amount) => ({
          amount: parseFloat(amount),
        })),
      });

      if (response.data.success) {
        setSubmissions((prev) => prev.map((submission) =>
          submission.id === selectedSubmission.id ? response.data.data : submission
        ));
        setSelectedSubmission(response.data.data);
        setMessageModalContent({ title: 'Success', message: 'Receipt amounts updated successfully.' });
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Failed to update receipt amounts:', err);
      setMessageModalContent({
        title: 'Error',
        message: 'Failed to update receipt amounts: ' + (err.response?.data?.error || err.message),
      });
      setShowMessageModal(true);
    } finally {
      setSavingAmounts(false);
    }
  };

  // Filter submissions based on status and search
  const filteredSubmissions = submissions.filter(submission => {
    const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
    const matchesSearch = !searchUserId || 
      submission.userId?.toLowerCase().includes(searchUserId.toLowerCase()) ||
      submission.username?.toLowerCase().includes(searchUserId.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  
  // Sort submissions by date
  const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedSubmissions.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSubmissions = sortedSubmissions.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, last page, and pages around current
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="submissions">
        <div className="loading-spinner">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="submissions">
      <div className="page-header">
        <h1>Receipt Ops</h1>
        <button className="btn-edit-rules" onClick={() => navigate('/rules')}>
          <IconEdit size={16} stroke={2} />
          Edit Rules Configuration
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-button ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          Submissions
        </button>
        <button 
          className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => navigate('/users')}
        >
          Users
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="submissions-container">
        <div className="submissions-controls">
          <div className="filter-section">
            <select 
              className="status-filter" 
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="processed">Processed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
              <option value="partial_failed">Partial Failed</option>
              <option value="reward_failed">Reward Failed</option>
            </select>
          </div>
          
          <div className="search-section">
            <div className="search-box">
              <IconSearch className="search-icon" size={16} stroke={2} />
              <input
                type="text"
                placeholder="Search by user ID or username"
                value={searchUserId}
                onChange={(e) => {
                  setSearchUserId(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {currentSubmissions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🧾</div>
            <h3>No Submissions Found</h3>
            <p>Try adjusting your filters or search criteria</p>
          </div>
        ) : (
          <>
            <div className="submissions-table-wrapper">
              <div className="table-header">
                <div className="th-date sortable" onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}>
                  Date & Time
                  <IconChevronDown 
                    className={`sort-icon ${sortOrder}`} 
                    size={16} 
                    stroke={2}
                  />
                </div>
                <div className="th-username">Username</div>
                <div className="th-userid">User ID</div>
                <div className="th-receipts">Receipts</div>
                <div className="th-status">Status</div>
                <div className="th-amount">Total Amount</div>
                <div className="th-reward">Reward Obtained</div>
                <div className="th-actions">Actions</div>
              </div>
              
              <div className="table-body">
                {currentSubmissions.map(submission => (
                  <div key={submission.id} className="table-row-card">
                    <div className="td-date">
                      <span className="date-cell">{formatDate(submission.createdAt)}</span>
                    </div>
                    <div className="td-username">
                      <span className="username-cell">{submission.username || '-'}</span>
                    </div>
                    <div className="td-userid">
                      <span className="user-id-preview" title={submission.userId || undefined}>
                        {formatUserIdPreview(submission.userId)}
                      </span>
                      {submission.userId ? (
                        <button
                          type="button"
                          className="btn-copy-userid"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyUserId(submission.userId, submission.id);
                          }}
                          title="Copy full user ID"
                          aria-label="Copy user ID"
                        >
                          {copiedUserIdKey === submission.id ? (
                            <span className="copy-done">✓</span>
                          ) : (
                            <IconCopy size={16} stroke={2} />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <div className="td-receipts">
                      <span className="receipt-count">
                        {submission.receipts?.length || 0} receipt(s)
                      </span>
                    </div>
                    <div className="td-status">
                      <span className={`status-badge ${getStatusBadge(submission.status)}`}>
                        {getStatusLabel(submission.status)}
                      </span>
                    </div>
                    <div className="td-amount">
                      <span className="amount">{formatAmount(submission.totalAmount)}</span>
                    </div>
                    <div className="td-reward">
                      <span className="reward-text">
                        {(() => {
                          // Check for voucher reward
                          const voucherName = submission.rewards?.voucher?.voucherName || 
                            submission.voucherSent?.ruleName;
                          const hasVoucher = voucherName || 
                            submission.rewards?.voucher?.nftId || 
                            submission.voucherSent?.voucherNftId;
                          
                          // Check for token reward
                          const tokenAmount = submission.rewards?.tokens?.amount;
                          const tokenType = submission.rewards?.tokens?.tokenType;
                          const hasToken = tokenAmount && tokenAmount > 0;
                          
                          // Display logic
                          if (hasVoucher && hasToken) {
                            // Both voucher and token
                            return `${voucherName || 'Voucher'} + ${tokenAmount} ${tokenType || 'Token'}`;
                          } else if (hasVoucher) {
                            // Only voucher
                            return voucherName || 'Voucher Sent';
                          } else if (hasToken) {
                            // Only token
                            return `${tokenAmount} ${tokenType || 'Token'}`;
                          } else {
                            return '-';
                          }
                        })()}
                      </span>
                    </div>
                    <div className="td-actions">
                      <div className="action-buttons-cell">
                        {canReview(submission) ? (
                          <button
                            type="button"
                            className="btn-claim-action btn-claim-action--verify"
                            onClick={() => openDetailModal(submission)}
                          >
                            Verify claim
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-claim-action btn-claim-action--details"
                            onClick={() => openDetailModal(submission)}
                          >
                            View details
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  <IconChevronsLeft size={16} stroke={2} />
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  <IconChevronLeft size={16} stroke={2} />
                </button>
                
                <div className="pagination-numbers">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  <IconChevronRight size={16} stroke={2} />
                </button>
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last page"
                >
                  <IconChevronsRight size={16} stroke={2} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header detail-modal-header">
              <h2>Submission Details</h2>
              <button type="button" className="modal-close" onClick={closeDetailModal} aria-label="Close">
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                {canReview(selectedSubmission) && (
                  <div className="review-notice" role="status">
                    <p className="review-notice__text">
                      This submission is awaiting manual review.
                    </p>
                  </div>
                )}
                {(selectedSubmission.reviewedBy ||
                  selectedSubmission.reviewedAt ||
                  selectedSubmission.rejectReason) && (
                  <div className="detail-grid-left detail-review-meta">
                    {selectedSubmission.reviewedBy && (
                      <div className="detail-item">
                        <label>REVIEWED BY</label>
                        <span>{selectedSubmission.reviewedBy}</span>
                      </div>
                    )}
                    {selectedSubmission.reviewedAt && (
                      <div className="detail-item">
                        <label>REVIEWED AT</label>
                        <span>{formatDate(selectedSubmission.reviewedAt)}</span>
                      </div>
                    )}
                    {selectedSubmission.rejectReason && (
                      <div className="detail-item full-width">
                        <label>REJECT REASON</label>
                        <span className="reject-reason">{selectedSubmission.rejectReason}</span>
                      </div>
                    )}
                  </div>
                )}
                <h3>Submission Info</h3>
                <div className="detail-grid-left">
                  <div className="detail-item">
                    <label>SUBMISSION ID</label>
                    <span>{selectedSubmission.id}</span>
                  </div>
                  <div className="detail-item detail-item--userid">
                    <label>USER ID</label>
                    <div className="detail-userid-row">
                      <span className="detail-userid-text" title={selectedSubmission.userId}>
                        {formatUserIdPreview(selectedSubmission.userId)}
                      </span>
                      {selectedSubmission.userId ? (
                        <button
                          type="button"
                          className="btn-copy-userid btn-copy-userid--detail"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyUserId(selectedSubmission.userId, 'detail-modal');
                          }}
                          title="Copy full user ID"
                          aria-label="Copy user ID"
                        >
                          {copiedUserIdKey === 'detail-modal' ? (
                            <span className="copy-done">✓</span>
                          ) : (
                            <IconCopy size={16} stroke={2} />
                          )}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>USERNAME</label>
                    <span>{selectedSubmission.username || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <label>DATE</label>
                    <span>{formatDate(selectedSubmission.createdAt)}</span>
                  </div>
                  <div className="detail-item">
                    <label>STATUS</label>
                    <span className={`status-badge ${getStatusBadge(selectedSubmission.status)}`}>
                      {getStatusLabel(selectedSubmission.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>TOTAL AMOUNT</label>
                    <span className="amount-large">
                      {formatAmount(canReview(selectedSubmission) ? getEditedTotalAmount() : selectedSubmission.totalAmount)}
                    </span>
                  </div>
                  {selectedSubmission.amountEditedBy && (
                    <div className="detail-item">
                      <label>AMOUNT EDITED BY</label>
                      <span>{selectedSubmission.amountEditedBy}</span>
                    </div>
                  )}
                  {selectedSubmission.amountEditedAt && (
                    <div className="detail-item">
                      <label>AMOUNT EDITED AT</label>
                      <span>{formatDate(selectedSubmission.amountEditedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Receipts ({selectedSubmission.receipts?.length || 0})</h3>
                {canReview(selectedSubmission) && (
                  <div className="receipt-edit-toolbar">
                    <p className="receipt-edit-hint">
                      You can adjust receipt amounts before approving this manual review submission.
                    </p>
                    <div className="receipt-edit-actions">
                      <button
                        type="button"
                        className="btn-save-amounts"
                        onClick={saveReceiptAmounts}
                        disabled={savingAmounts || !hasReceiptAmountChanges()}
                      >
                        {savingAmounts ? 'Saving...' : 'Save Amounts'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="receipts-grid">
                  {selectedSubmission.receipts?.map((receipt, index) => (
                    <div key={index} className="receipt-card">
                      {receipt.image && (
                        <div className="receipt-preview-wrap">
                          <img 
                            src={receipt.image} 
                            alt={`Receipt ${index + 1}`}
                            className="receipt-image"
                            onClick={() => openImageViewer(receipt.image)}
                          />
                          <button
                            type="button"
                            className="receipt-enlarge-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageViewer(receipt.image);
                            }}
                            title="Enlarge receipt"
                          >
                            <IconZoomIn size={18} stroke={2} />
                            <span>Enlarge</span>
                          </button>
                        </div>
                      )}
                      <div className="receipt-info">
                        {canReview(selectedSubmission) ? (
                          <div className="receipt-amount-editor">
                            <label className="receipt-amount-label" htmlFor={`receipt-amount-${index}`}>
                              Receipt amount
                            </label>
                            <div className="receipt-amount-input-row">
                              <span className="receipt-amount-prefix">RM</span>
                              <input
                                id={`receipt-amount-${index}`}
                                type="number"
                                min="0"
                                step="0.01"
                                className="receipt-amount-input"
                                value={getCurrentReceiptAmounts()[index] ?? ''}
                                onChange={(e) => handleReceiptAmountChange(index, e.target.value)}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="receipt-amount">{formatAmount(receipt.amount)}</span>
                        )}
                        {receipt.date && (
                          <div className="receipt-detail">
                            <label>Receipt Date:</label>
                            <span>{new Date(receipt.date).toLocaleDateString()}</span>
                          </div>
                        )}
                        {receipt.storeName && (
                          <div className="receipt-detail">
                            <label>Store:</label>
                            <span>{receipt.storeName}</span>
                          </div>
                        )}
                        {receipt.storeNameOther && (
                          <div className="receipt-detail">
                            <label>Store Name:</label>
                            <span>{receipt.storeNameOther}</span>
                          </div>
                        )}
                        {receipt.ocrRaw && (
                          <details className="ocr-details">
                            <summary>OCR Text</summary>
                            <pre>{receipt.ocrRaw}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rewards Section */}
              <div className="detail-section">
                <h3>
                  Rewards
                  {selectedSubmission.rewards?.status ? (
                    <span className={`rewards-status-badge ${selectedSubmission.rewards.status}`}>
                      {selectedSubmission.rewards.status === 'success' ? '✓ Success' :
                       selectedSubmission.rewards.status === 'partial_failed' ? '⚠ Partial' :
                       selectedSubmission.rewards.status === 'failed' ? '✗ Failed' :
                       selectedSubmission.rewards.status}
                    </span>
                  ) : (canReview(selectedSubmission) ||
                    (selectedSubmission.rewards && !selectedSubmission.rewards.status)) ? (
                    <span className="rewards-status-badge pending-approval">Pending approval</span>
                  ) : null}
                </h3>
                <div className="rewards-section-intro">
                  <p className="rewards-section-hint rewards-section-hint--lead">
                    {rewardsIntroLeadText(selectedSubmission)}
                  </p>
                  {canReview(selectedSubmission) && !selectedSubmission.rewards && (
                    <p className="rewards-section-hint rewards-pre-approval-note">
                      Voucher and token lines appear below once this claim is approved and rewards are issued.
                    </p>
                  )}
                  {canResend(selectedSubmission) && (
                    <p className="rewards-section-hint">
                      One or more rewards could not be delivered. Use{' '}
                      <strong>Resend failed rewards</strong> in the bar below to try again.
                    </p>
                  )}
                </div>

                {(() => {
                  const rewardRows = getRewardsDetailRows(selectedSubmission);
                  if (rewardRows.length === 0) return null;
                  const showErrorReasonColumn = rewardRows.some((row) => row.errorReason);
                  return (
                    <div className="rewards-detail-table-scroll">
                      <table className="rewards-detail-table">
                        <colgroup>
                          <col className="rewards-detail-col--type" />
                          <col className="rewards-detail-col--name" />
                          <col
                            className={
                              showErrorReasonColumn
                                ? 'rewards-detail-col--nft'
                                : 'rewards-detail-col--nft rewards-detail-col--nft--no-error-col'
                            }
                          />
                          <col className="rewards-detail-col--status" />
                          {showErrorReasonColumn && <col className="rewards-detail-col--error" />}
                          <col
                            className={
                              showErrorReasonColumn
                                ? 'rewards-detail-col--sent'
                                : 'rewards-detail-col--sent rewards-detail-col--sent--no-error-col'
                            }
                          />
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">Reward type</th>
                            <th scope="col">Voucher name</th>
                            <th scope="col">NFT ID</th>
                            <th scope="col">Status</th>
                            {showErrorReasonColumn && <th scope="col">Error reason</th>}
                            <th scope="col">Sent at</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rewardRows.map((row) => (
                            <tr key={row.key}>
                              <td>
                                <span className="reward-type-chip">{row.typeChip}</span>
                              </td>
                              <td
                                className="rewards-detail-table__name"
                                title={row.title || (row.name !== '—' ? row.name : undefined)}
                              >
                                {row.name}
                              </td>
                              <td
                                className="rewards-detail-table__nft"
                                title={row.nftId !== '—' ? row.nftId : undefined}
                              >
                                {row.nftId}
                              </td>
                              <td>
                                <span className={`reward-status-chip reward-status-chip--${row.statusVariant}`}>
                                  {row.statusText}
                                </span>
                              </td>
                              {showErrorReasonColumn && (
                                <td
                                  className={`rewards-detail-table__error${
                                    row.errorReason ? ' rewards-detail-table__error--failed' : ''
                                  }`}
                                  title={row.errorReason || undefined}
                                >
                                  {row.errorReason
                                    ? row.errorReason.length > 200
                                      ? `${row.errorReason.slice(0, 199)}…`
                                      : row.errorReason
                                    : '—'}
                                </td>
                              )}
                              <td className="rewards-detail-table__sent">{row.sentAt}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {canResend(selectedSubmission) &&
                  selectedSubmission.resendAttempts?.length > 0 && (
                    <p className="resend-attempts-hint">
                      Previous resend attempts: {selectedSubmission.resendAttempts.length}
                      {selectedSubmission.lastResendAt && (
                        <span> (Last: {formatDate(selectedSubmission.lastResendAt)})</span>
                      )}
                    </p>
                  )}
              </div>

              {selectedSubmission.error && (
                <div className="detail-section error-section">
                  <h3>System Error</h3>
                  <p className="error-text">{selectedSubmission.error}</p>
                </div>
              )}
            </div>

            {(canReview(selectedSubmission) || canResend(selectedSubmission)) && (
              <div className="detail-modal-footer">
                <div className="detail-modal-footer__actions">
                  {canReview(selectedSubmission) && (
                    <>
                      <button
                        type="button"
                        className="btn-detail-primary"
                        onClick={() => handleApprove(selectedSubmission.id)}
                        disabled={approving || rejecting}
                      >
                        {approving ? 'Approving…' : 'Approve & send rewards'}
                      </button>
                      <button
                        type="button"
                        className="btn-detail-danger"
                        onClick={() => handleReject(selectedSubmission.id)}
                        disabled={approving || rejecting}
                      >
                        {rejecting ? 'Rejecting…' : 'Reject'}
                      </button>
                    </>
                  )}
                  {canResend(selectedSubmission) && (
                    <button
                      type="button"
                      className="btn-detail-primary"
                      onClick={() => handleResend(selectedSubmission.id)}
                      disabled={resending}
                    >
                      {resending ? 'Resending…' : 'Resend failed rewards'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Viewer (lightbox with zoom) */}
      {viewingImage && (
        <div
          className="image-viewer-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Receipt preview"
          onClick={closeImageViewer}
        >
          <button
            type="button"
            className="image-viewer-close"
            onClick={(e) => {
              e.stopPropagation();
              closeImageViewer();
            }}
            aria-label="Close"
          >
            <IconX size={22} stroke={2} />
          </button>
          <div
            className="image-viewer-toolbar"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="image-viewer-tool-btn"
              onClick={() => setViewerZoomValue(Math.round((viewerZoomRef.current - 0.25) * 100) / 100)}
              title="Zoom out"
              aria-label="Zoom out"
            >
              <IconMinus size={18} stroke={2} />
            </button>
            <span className="image-viewer-zoom-label">{Math.round(viewerZoom * 100)}%</span>
            <button
              type="button"
              className="image-viewer-tool-btn"
              onClick={() => setViewerZoomValue(Math.round((viewerZoomRef.current + 0.25) * 100) / 100)}
              title="Zoom in"
              aria-label="Zoom in"
            >
              <IconPlus size={18} stroke={2} />
            </button>
            <button
              type="button"
              className="image-viewer-tool-btn image-viewer-tool-reset"
              onClick={resetImageViewerTransform}
              title="Reset zoom"
            >
              Reset
            </button>
          </div>
          <div
            className="image-viewer-scroll"
            onClick={(e) => e.stopPropagation()}
            onWheel={handleViewerWheel}
            onPointerDown={handleViewerPointerDown}
            onPointerMove={handleViewerPointerMove}
            onPointerUp={handleViewerPointerEnd}
            onPointerCancel={handleViewerPointerEnd}
            onLostPointerCapture={handleViewerPointerEnd}
            onDoubleClick={handleViewerDoubleClick}
          >
            <div
              className="image-viewer-inner"
              style={{
                transform: `translate3d(${viewerPan.x}px, ${viewerPan.y}px, 0) scale(${viewerZoom})`,
                transformOrigin: 'center center',
              }}
            >
              <img
                src={viewingImage}
                alt="Receipt enlarged view"
                className="image-viewer-image"
                draggable={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowApproveModal(false); setPendingAction(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Approve Submission</h2>
            <p className="auth-modal-message">Are you sure you want to approve this submission? Rewards will be sent to the user.</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowApproveModal(false); setPendingAction(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmApprove}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Confirmation Modal */}
      {showRejectModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowRejectModal(false); setPendingAction(null); setRejectReason(''); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Reject Submission</h2>
            <p className="auth-modal-message" style={{ marginBottom: '1rem' }}>Please enter a reason for rejection:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter rejection reason..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '0.75rem',
                border: '1px solid #cbd5e0',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '1rem'
              }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowRejectModal(false); setPendingAction(null); setRejectReason(''); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmReject}
                style={{ 
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Confirmation Modal */}
      {showResendModal && (
        <div className="auth-modal-overlay" onClick={() => { setShowResendModal(false); setPendingAction(null); }}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">Resend Rewards</h2>
            <p className="auth-modal-message">Are you sure you want to resend the failed rewards?</p>
            <div style={{ display: 'flex', gap: '0.75rem', width: '100%', marginTop: '1rem' }}>
              <button 
                className="auth-modal-button"
                onClick={() => { setShowResendModal(false); setPendingAction(null); }}
                style={{ 
                  background: 'white', 
                  color: '#2d3748', 
                  border: '1px solid #cbd5e0',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Cancel
              </button>
              <button 
                className="auth-modal-button"
                onClick={confirmResend}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  maxWidth: 'none',
                  flex: 1
                }}
              >
                Resend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal (Success/Error) */}
      {showMessageModal && (
        <div className="auth-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="auth-modal-title">{messageModalContent.title}</h2>
            <p className="auth-modal-message">{messageModalContent.message}</p>
            <button 
              className="auth-modal-button"
              onClick={() => setShowMessageModal(false)}
              style={{ 
                background: messageModalContent.title === 'Error' 
                  ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <CopyToast open={copyUserIdToast.open} />
    </div>
  );
}

export default Submissions;
